import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Hammer, CheckCircle, Settings, Zap, AlertTriangle, Wrench, Shield } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import rocks from '../../assets/Images/services/onsite1.webp'

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.2
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};

const RockBreakerRefurbishment = () => {
  const navigate = useNavigate();

  const keyPoints = [
    'Complete overhaul and servicing of all rock breaker models',
    'Replacement of pistons, bushings, diaphragms, and seals with genuine spares',
    'High-pressure and nitrogen testing for accurate impact force',
    'Field-tested to ensure durability and strike consistency',
    'Extends breaker life and restores maximum hitting power'
  ];

  const processSteps = [
    {
      icon: <Wrench className="text-blue-600" size={24} />,
      title: 'Disassembly & Inspection',
      description: 'Rock breaker is dismantled; wear, cracks, and pressure leaks identified.'
    },
    {
      icon: <Hammer className="text-blue-600" size={24} />,
      title: 'Component Restoration',
      description: 'Damaged parts repaired or replaced with original components.'
    },
    {
      icon: <Shield className="text-blue-600" size={24} />,
      title: 'Seal & Diaphragm Replacement',
      description: 'Ensures no internal leakage or loss of impact energy.'
    },
    {
      icon: <AlertTriangle className="text-blue-600" size={24} />,
      title: 'Hydraulic & Nitrogen Testing',
      description: 'Tested for correct operating pressure and striking force.'
    },
    {
      icon: <Settings className="text-blue-600" size={24} />,
      title: 'Reassembly & Trial Run',
      description: 'Final assembly verified under simulated work conditions.'
    },
    {
      icon: <CheckCircle className="text-blue-600" size={24} />,
      title: 'Quality Documentation',
      description: 'Performance reports shared with client for transparency.'
    }
  ];

  const objectives = [
    'Restore impact performance equal to or better than OEM standards',
    'Improve reliability and reduce downtime on field operations',
    'Ensure safe and efficient functioning of hydraulic breakers',
    'Provide cost-effective alternatives to full equipment replacement',
    'Guarantee sustained performance and durability with genuine parts'
  ];

  // Animation controls
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('show');
    }
  }, [controls, inView]);

  return (
    <div className="text-gray-800 leading-relaxed max-w-full overflow-x-hidden pt-20 md:pt-24">
      <Header />
      
      {/* Hero Section */}
      <motion.section 
        initial="hidden"
        animate="show"
        variants={container}
        className="relative bg-gray-50 py-16 md:py-24 overflow-hidden"
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            {/* Left side - Text content */}
            <motion.div 
              variants={item}
              className="md:w-1/2 mb-10 md:mb-0 md:pr-10"
            >
              <motion.h1 
                variants={fadeInUp}
                className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00325b] mb-4 sm:mb-6"
              >
                Rock Breaker Refurbishment
              </motion.h1>
              <motion.p 
                variants={fadeInUp}
                className="text-lg sm:text-xl text-gray-700 mb-6 sm:mb-8"
              >
                Expert refurbishment service restoring peak impact power and performance to all major rock breaker models.
              </motion.p>
              <motion.div 
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4"
              >
                <motion.button 
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition-colors duration-300"
                >
                  Get a Free Quote
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/contact')}
                  className="flex items-center justify-center gap-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium py-3 px-6 rounded-lg transition-colors duration-300"
                >
                  <Shield size={20} />
                  Service Enquiry
                </motion.button>
              </motion.div>
            </motion.div>
            
            {/* Right side - Image */}
            <motion.div 
              variants={item}
              className="md:w-1/2 relative"
            >
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="relative rounded-xl overflow-hidden shadow-2xl"
              >
                <motion.img 
                  src={rocks}
                  alt="Rock Breaker Refurbishment"
                  className="w-full h-auto rounded-xl transform hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent rounded-xl"></div>
              </motion.div>
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: 'spring', stiffness: 100 }}
                className="hidden md:block absolute -bottom-6 -left-6 w-32 h-32 bg-blue-100 rounded-full -z-10"
              ></motion.div>
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.7, type: 'spring', stiffness: 100 }}
                className="hidden md:block absolute -top-6 -right-6 w-24 h-24 bg-yellow-100 rounded-full -z-10"
              ></motion.div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Description Section */}
      <motion.section 
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={container}
        className="relative py-20 bg-gradient-to-br from-blue-50 to-gray-50 overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <motion.div 
            initial={{ x: -100, opacity: 0 }}
            animate={inView ? { x: 0, opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="absolute top-20 -left-20 w-64 h-64 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl"
          ></motion.div>
          <motion.div 
            initial={{ x: 100, opacity: 0 }}
            animate={inView ? { x: 0, opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="absolute bottom-20 -right-20 w-64 h-64 bg-yellow-400 rounded-full mix-blend-multiply filter blur-3xl"
          ></motion.div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            variants={container}
            className="max-w-5xl mx-auto text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Professional <span className="text-blue-600">Rock Breaker Solutions</span>
            </motion.h2>
            <motion.div 
              variants={fadeInUp}
              className="w-24 h-1.5 bg-blue-600 mx-auto mb-8"
            ></motion.div>
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed"
            >
              We specialize in complete overhauls using genuine spares, ensuring maximum hitting energy and reduced downtime for your rock breakers.
            </motion.p>
          </motion.div>
          
          <motion.div 
            variants={container}
            className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto px-4"
          >
            {/* Why Choose Us - Left Side */}
            <motion.div 
              variants={item}
              whileHover={{ y: -5 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 h-full"
            >
              <motion.div 
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className="bg-blue-50 w-14 h-14 rounded-xl flex items-center justify-center mb-6"
              >
                <Zap className="text-blue-600" size={28} />
              </motion.div>
              <motion.h3 
                variants={fadeInUp}
                className="text-2xl font-semibold mb-6 text-[#00325b]"
              >
                Why Choose Us
              </motion.h3>
              <motion.ul 
                variants={container}
                className="space-y-4"
              >
                {keyPoints.map((point, index) => (
                  <motion.li 
                    key={index} 
                    variants={item}
                    className="flex items-start group"
                  >
                    <motion.div 
                      whileHover={{ rotate: 10, scale: 1.1 }}
                      className="bg-green-100 p-1.5 rounded-full mr-3 mt-0.5 group-hover:bg-green-200 transition-colors"
                    >
                      <CheckCircle className="text-green-600" size={18} />
                    </motion.div>
                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors">{point}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
            
            {/* Our Objectives - Right Side */}
            <motion.div 
              variants={item}
              whileHover={{ y: -5 }}
              className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 h-full"
            >
              <motion.div 
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.2 }}
                className="bg-blue-50 w-14 h-14 rounded-xl flex items-center justify-center mb-6"
              >
                <Settings className="text-blue-600" size={28} />
              </motion.div>
              <motion.h3 
                variants={fadeInUp}
                className="text-2xl font-semibold mb-6 text-[#00325b]"
              >
                Our Objectives
              </motion.h3>
              <motion.ul 
                variants={container}
                className="space-y-4"
              >
                {objectives.map((objective, index) => (
                  <motion.li 
                    key={index} 
                    variants={item}
                    className="flex items-start group"
                  >
                    <motion.div 
                      whileHover={{ rotate: 10, scale: 1.1 }}
                      className="bg-green-100 p-1.5 rounded-full mr-3 mt-0.5 group-hover:bg-green-200 transition-colors"
                    >
                      <CheckCircle className="text-green-600" size={18} />
                    </motion.div>
                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors">{objective}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Process Section */}
      {/* <motion.section 
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={container}
        className="py-16 bg-white"
      >
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div 
            variants={container}
            className="text-center mb-12 px-4"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl font-bold text-[#00325b] mb-4"
            >
              Our Refurbishment Process
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-gray-600 max-w-3xl mx-auto"
            >
              We follow a detailed process to ensure your rock breaker is restored to its optimal performance.
            </motion.p>
          </motion.div>
          
          <motion.div 
            variants={container}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-4"
          >
            {processSteps.map((step, index) => (
              <motion.div 
                key={index} 
                variants={item}
                whileHover={{ y: -5, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
                className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
              >
                <motion.div 
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20, delay: index * 0.1 }}
                  className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4"
                >
                  {React.cloneElement(step.icon, { className: 'text-blue-600' })}
                </motion.div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section> */}

      {/* Features Section */}
      <motion.section 
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={container}
        className="py-16 bg-gray-50"
      >
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div 
            variants={container}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl font-bold text-[#00325b] mb-4"
            >
              Service Benefits
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-gray-600 max-w-3xl mx-auto"
            >
              Why choose our rock breaker refurbishment service
            </motion.p>
          </motion.div>
          
          <motion.div 
            variants={container}
            className="grid md:grid-cols-3 gap-8"
          >
            <motion.div 
              variants={item}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="text-blue-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Genuine Parts</h3>
              <p className="text-gray-600">We use only OEM or equivalent quality parts for all repairs and replacements.</p>
            </motion.div>
            
            <motion.div 
              variants={item}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Zap className="text-blue-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Restored Performance</h3>
              <p className="text-gray-600">Bring your rock breaker back to its original hitting power and efficiency.</p>
            </motion.div>
            
            <motion.div 
              variants={item}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle className="text-blue-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Warranty Backed</h3>
              <p className="text-gray-600">All our refurbishment work comes with a comprehensive warranty.</p>
            </motion.div>
            
            <motion.div 
              variants={item}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="text-blue-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Expert Technicians</h3>
              <p className="text-gray-600">Our certified technicians have years of experience in rock breaker refurbishment.</p>
            </motion.div>
            
            <motion.div 
              variants={item}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Settings className="text-blue-600" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Quick Turnaround</h3>
              <p className="text-gray-600">Efficient service to minimize your equipment downtime.</p>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>
      
      {/* CTA Section */}
      <section id="contact" className="bg-blue-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Rock Breaker Service?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us today for professional rock breaker refurbishment and repair services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-white text-blue-700 hover:bg-gray-100 font-medium py-3 px-8 rounded-lg transition-colors duration-300"
            >
              Request Service
            </button>
            {/* <a 
              href="tel:+1234567890"
              className="bg-transparent border-2 border-white hover:bg-white/10 font-medium py-3 px-8 rounded-lg transition-colors duration-300"
            >
              Call Now: (123) 456-7890
            </a> */}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RockBreakerRefurbishment;
